/**
 * Teacher Home (docs/06-UI-SPEC.md §5).
 *
 * Two actions of equal weight and the recent lessons strip. The recent list is empty
 * until lessons can be archived (Phase 7), so it shows the empty state from §17.2.
 */

import { useEffect, useState, type ReactNode } from 'react';

import type { GradeBand } from '@opal/shared';

import {
  fetchRecoveredLessons,
  startLesson,
  type RecoveredLesson,
  type StartedLesson,
} from '../api.js';
import { FolderIcon, LessonsEmptyIcon, PlusIcon } from '../assets/icons.js';
import { HomeHeader } from '../components/HomeHeader.js';
import { RecoveryBanner } from '../components/RecoveryBanner.js';
import { TopicDialog } from '../components/TopicDialog.js';
import { noticeToShow } from '../recovery-notice.js';
import { navigate, useQueryParam } from '../router.js';
import { signOut, useTeacherSession } from '../session.js';

interface ActionCardProps {
  readonly icon: ReactNode;
  readonly label: string;
  readonly onClick: () => void;
}

function ActionCard({ icon, label, onClick }: ActionCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="
        flex h-[180px] w-[240px] max-w-full flex-col items-center justify-center gap-4
        rounded-xl border border-border bg-surface text-text-primary shadow-sm
        transition duration-fast ease-out
        hover:-translate-y-[2px] hover:shadow-md
        active:translate-y-0
        max-[640px]:w-full
      "
    >
      <span className="text-text-secondary">{icon}</span>
      <span className="text-lg font-semibold">{label}</span>
    </button>
  );
}

interface HomeBodyProps {
  readonly problem: string | null;
  readonly drive: string | null;
  readonly recovered: RecoveredLesson | null;
  readonly onDismissRecovered: () => void;
  readonly onStartNew: () => void;
}

/** Everything below the header. */
function HomeBody({ problem, drive, recovered, onDismissRecovered, onStartNew }: HomeBodyProps) {
  return (
    <main className="mx-auto flex max-w-[840px] flex-col items-center gap-12 p-12 max-[640px]:p-6">
      {problem === null ? null : <p className="text-base text-danger">{problem}</p>}
      {drive === null ? null : (
        <p className="max-w-[640px] text-base text-text-secondary">{drive}</p>
      )}
      {recovered === null ? null : (
        <RecoveryBanner
          lesson={recovered}
          onView={() => {
            navigate('/lectures');
          }}
          onDismiss={onDismissRecovered}
        />
      )}
      <HomeActions onStartNew={onStartNew} />
      <RecentLessons />
    </main>
  );
}

function HomeActions({ onStartNew }: { readonly onStartNew: () => void }) {
  return (
    <div className="flex w-full max-w-[640px] flex-wrap items-center justify-center gap-6">
      <ActionCard icon={<PlusIcon size={40} />} label="Start New Lesson" onClick={onStartNew} />
      <ActionCard
        icon={<FolderIcon size={40} />}
        label="Open Previous Lesson"
        onClick={() => {
          navigate('/lectures');
        }}
      />
    </div>
  );
}

function RecentLessons() {
  return (
    <section className="flex w-full max-w-[640px] flex-col gap-4">
      <h2 className="text-lg font-semibold text-text-primary">Recent lessons</h2>
      <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-surface p-12 text-center">
        <span className="text-text-placeholder">
          <LessonsEmptyIcon size={40} />
        </span>
        <p className="text-base text-text-secondary">Your finished lessons will appear here</p>
      </div>
    </section>
  );
}

/** Shown while the server is being asked who is signed in — never a blank screen (§17.1). */
function CheckingScreen() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-sunken p-6">
      <p className="text-base text-text-secondary">Opening your home page…</p>
    </main>
  );
}

/**
 * SRS I2 — the lesson the teacher never closed, if there is one.
 *
 * Asked for once the teacher is known, and never allowed to fail loudly: this is
 * news about a lesson that is already safe, so a notice that cannot be fetched must
 * not be what stops the home page appearing.
 */
function useRecoveredLesson(signedIn: boolean) {
  const [lesson, setLesson] = useState<RecoveredLesson | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    void fetchRecoveredLessons().then((lessons) => {
      setLesson(noticeToShow(lessons));
    });
  }, [signedIn]);

  return {
    lesson,
    dismiss: () => {
      setLesson(null);
    },
  };
}

/**
 * What to say after a teacher comes back from Google's Drive consent screen (SRS I4).
 *
 * Nothing at all unless they have just been there — the home page is not the place
 * to talk about filing on any other day.
 */
function driveMessage(outcome: string | null): string | null {
  if (outcome === 'connected') {
    return 'Google Drive is connected. Your finished lessons will be saved there.';
  }
  if (outcome === 'failed') {
    return "Google Drive wasn't connected. Your lessons are still saved here — you can try again when a lesson ends.";
  }
  return null;
}

/** The header, with the two things a teacher can reach from anywhere on this page. */
function HomeTopBar({ email }: { readonly email: string }) {
  return (
    <HomeHeader
      email={email}
      onOpenLectureRecord={() => {
        navigate('/lectures');
      }}
      onSignOut={() => {
        void signOut().then(() => {
          navigate('/');
        });
      }}
    />
  );
}

/** Goes to the new board, carrying what the board screen needs to open it. */
function openBoard(topic: string, gradeBand: GradeBand, lesson: StartedLesson): void {
  navigate('/board', {
    topic,
    grade: gradeBand,
    code: lesson.roomCode,
    board: lesson.boardId,
  });
}

export function TeacherHomePage() {
  const session = useTeacherSession();
  const [askingTopic, setAskingTopic] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const recovered = useRecoveredLesson(session.status === 'signed-in');
  const driveOutcome = useQueryParam('drive');

  useEffect(() => {
    // A teacher whose 30-day session has run out goes back to sign-in, not to a dead end.
    if (session.status === 'signed-out') navigate('/teacher');
  }, [session.status]);

  if (session.status !== 'signed-in') return <CheckingScreen />;

  const onStart = (topic: string, gradeBand: GradeBand) => {
    setAskingTopic(false);
    setProblem(null);
    void startLesson(topic, gradeBand).then((result) => {
      if (!result.ok) {
        setProblem(result.message);
        return;
      }
      openBoard(topic, gradeBand, result.value);
    });
  };

  return (
    <div className="min-h-screen bg-surface-sunken">
      <HomeTopBar email={session.teacher.email} />
      <HomeBody
        problem={problem}
        drive={driveMessage(driveOutcome)}
        recovered={recovered.lesson}
        onDismissRecovered={recovered.dismiss}
        onStartNew={() => {
          setAskingTopic(true);
        }}
      />
      {askingTopic ? (
        <TopicDialog
          onStart={onStart}
          onClose={() => {
            setAskingTopic(false);
          }}
        />
      ) : null}
    </div>
  );
}
